import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Flex, HStack, Icon, Text, usePrefersReducedMotion, } from '@spothero/ui';
import IconPriceTag from '@spothero/icons/price-tag';
import { motion } from 'framer-motion';
import StorageUtils from '@spothero/utils/storage';
import { StorageKeys, StorageTypes } from 'common/constants/storageKeys';
import { GradientText } from './GradientText';
const ENTRANCE_DELAY = 1.2;
const ENTRANCE_DURATION = 0.6;
const SPRING_STIFFNESS = 300;
const SPRING_DAMPING = 20;
const SPRING_MASS = 1;
const GRADIENT_DELAY = ENTRANCE_DELAY + 0.1;
const GRADIENT_DURATION = 3;
const GRADIENT_FADE_OUT_DURATION = 0.2;
const FINAL_TEXT_FADE_DURATION = 0.3;
const AUTO_CLOSE_DELAY_MS = 2000;
const FADE_OUT_DURATION = 0.3;
const CLOSE_BUTTON_LABEL = 'Close';
const CALL_OUT_TEXT = 'Total price includes all required fees';
const MotionBox = motion(Box);
const MotionText = motion(Text);
export function AllInPricingCalloutMobile({ onAnimationComplete, }) {
    const prefersReducedMotion = usePrefersReducedMotion();
    const [finalRevealComplete, setFinalRevealComplete] = useState(prefersReducedMotion);
    const [isFadingOut, setIsFadingOut] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);
    const hasMarkedShownRef = useRef(false);
    // Check if the callout has been shown before in this session
    const hasBeenShownOnMountRef = useRef(StorageUtils.get(StorageKeys.ALL_IN_PRICING_CALLOUT_SHOWN, StorageTypes.SESSION) === true);
    const hasBeenShownOnMount = hasBeenShownOnMountRef.current;
    const markAsShown = useCallback(() => {
        if (hasMarkedShownRef.current) {
            return;
        }
        hasMarkedShownRef.current = true;
        StorageUtils.set(StorageKeys.ALL_IN_PRICING_CALLOUT_SHOWN, true, StorageTypes.SESSION);
        if (onAnimationComplete) {
            onAnimationComplete();
        }
    }, [onAnimationComplete]);
    const handleFinalRevealComplete = useCallback(() => {
        setFinalRevealComplete(true);
        markAsShown();
    }, [markAsShown]);
    const startFadeOut = useCallback(() => {
        setIsFadingOut(previous => previous || true);
    }, []);
    const handleDismiss = () => {
        handleFinalRevealComplete();
        startFadeOut();
    };
    useEffect(() => {
        if (hasBeenShownOnMount) {
            return;
        }
        if (prefersReducedMotion) {
            handleFinalRevealComplete();
        }
    }, [hasBeenShownOnMount, prefersReducedMotion, handleFinalRevealComplete]);
    useEffect(() => {
        if (hasBeenShownOnMount ||
            !finalRevealComplete ||
            isFadingOut ||
            typeof window === 'undefined') {
            return;
        }
        const timeoutId = window.setTimeout(() => {
            startFadeOut();
        }, AUTO_CLOSE_DELAY_MS);
        return () => window.clearTimeout(timeoutId);
    }, [hasBeenShownOnMount, finalRevealComplete, isFadingOut, startFadeOut]);
    useEffect(() => {
        if (hasBeenShownOnMount || !isFadingOut) {
            return;
        }
        if (typeof window === 'undefined' || prefersReducedMotion) {
            setIsDismissed(true);
            return;
        }
        const timeoutId = window.setTimeout(() => {
            setIsDismissed(true);
        }, FADE_OUT_DURATION * 1000);
        return () => window.clearTimeout(timeoutId);
    }, [hasBeenShownOnMount, isFadingOut, prefersReducedMotion]);
    if (isDismissed || hasBeenShownOnMount) {
        return null;
    }
    const entranceDelay = prefersReducedMotion ? 0 : ENTRANCE_DELAY;
    const slideTransition = prefersReducedMotion
        ? { duration: 0 }
        : {
            delay: entranceDelay,
            type: 'spring',
            stiffness: SPRING_STIFFNESS,
            damping: SPRING_DAMPING,
            mass: SPRING_MASS,
        };
    const opacityTransition = isFadingOut
        ? {
            duration: prefersReducedMotion ? 0 : FADE_OUT_DURATION,
            ease: 'easeInOut',
        }
        : {
            delay: entranceDelay,
            duration: prefersReducedMotion ? 0 : ENTRANCE_DURATION,
            ease: 'easeOut',
        };
    const gradientFadeOutDelay = prefersReducedMotion
        ? 0
        : GRADIENT_DELAY + GRADIENT_DURATION;
    const finalRevealDelay = gradientFadeOutDelay;
    const finalRevealDuration = prefersReducedMotion
        ? 0
        : FINAL_TEXT_FADE_DURATION;
    return (<Flex data-testid="AllInPricingCalloutMobile" flexGrow={1} justifyContent="center" position="fixed" bottom={0} left={0} right={0} zIndex={1000} paddingX={4} paddingBottom={4}>
            <MotionBox initial={{
            y: prefersReducedMotion ? 0 : 100,
            opacity: prefersReducedMotion ? 1 : 0,
            backgroundColor: '#000000',
        }} animate={{
            y: 0,
            opacity: isFadingOut ? 0 : 1,
            backgroundColor: '#000000',
        }} transition={{
            y: slideTransition,
            opacity: opacityTransition,
        }} borderRadius="md" paddingX={4} paddingY={3} display="inline-block" boxShadow="lg" width="100%">
                <HStack spacing={0} align="center" justify="space-between">
                    <HStack spacing={0} align="center" flex={1}>
                        <MotionBox marginTop={2} initial={{
            color: prefersReducedMotion
                ? 'white'
                : '#52A8FB',
        }} animate={{ color: 'white' }} transition={{
            delay: prefersReducedMotion
                ? 0
                : GRADIENT_DELAY,
            duration: prefersReducedMotion
                ? 0
                : GRADIENT_DURATION,
            ease: 'easeInOut',
        }}>
                            <Icon as={IconPriceTag} boxSize={4}/>
                        </MotionBox>
                        <Box position="relative" pl={2} minWidth="max-content">
                            <Box fontSize="sm" fontWeight="semibold" whiteSpace="nowrap">
                                {!prefersReducedMotion && (<MotionBox initial={{ opacity: 1 }} animate={{ opacity: 0 }} transition={{
                delay: gradientFadeOutDelay,
                duration: GRADIENT_FADE_OUT_DURATION,
                ease: 'easeInOut',
            }} sx={{
                position: 'absolute',
                top: 0,
                left: 1,
                right: 0,
            }}>
                                        <GradientText animationSpeed={GRADIENT_DURATION} fontWeight="semibold">
                                            {CALL_OUT_TEXT}
                                        </GradientText>
                                    </MotionBox>)}

                                <MotionText color="#FFFFFF" fontSize="sm" fontWeight="semibold" marginLeft={prefersReducedMotion ? 0 : -1} initial={{
            opacity: prefersReducedMotion ? 1 : 0,
        }} animate={{ opacity: 1 }} transition={{
            opacity: {
                delay: finalRevealDelay,
                duration: finalRevealDuration,
                ease: 'easeInOut',
            },
        }} onAnimationComplete={handleFinalRevealComplete}>
                                    {CALL_OUT_TEXT}
                                </MotionText>
                            </Box>
                        </Box>
                    </HStack>
                    <Box as="button" aria-label={CLOSE_BUTTON_LABEL} onClick={handleDismiss} marginLeft={2} padding={2} cursor="pointer" fontSize="xl" lineHeight={1} color="white" _hover={{ color: 'gray.300' }} background="transparent" border="none">
                        ×
                    </Box>
                </HStack>
            </MotionBox>
        </Flex>);
}
